import { Link } from 'react-router-dom'
import { useAdminDeleteCall } from '@/entities/admin'
import type { AdminCallListItem } from '@/shared/api/types'
import styles from '../AdminPages.module.css'
import { ElevenLabsCost } from './ElevenLabsCost'
import { formatApproxUsd } from './adminCosts'
import {
  callKindLabel,
  callStatusLabel,
  callStatusTone,
  formatAdminDate,
  formatDurationMs,
} from './format'

interface AdminCallsTableProps {
  items: AdminCallListItem[]
  elevenLabsUsdPer1kCredits: number
  showCompany?: boolean
}

export function AdminCallsTable({
  items,
  elevenLabsUsdPer1kCredits,
  showCompany = true,
}: AdminCallsTableProps) {
  return (
    <>
      <div className={`${styles.tableWrap} ${styles.desktopTable}`}>
        <table className={`${styles.table} ${styles.callsTable}`}>
          <thead>
            <tr>
              <th>Звонок</th>
              {showCompany && <th>Компания</th>}
              <th>Тип</th>
              <th>Состояние</th>
              <th>Телефон</th>
              <th>Создан</th>
              <th>Длительность</th>
              <th>Списано</th>
              <th>Синтез</th>
              <th>Стоимость</th>
              <th>Говорил агент</th>
              <th>Говорил клиент</th>
              <th>Ошибка</th>
              <th>Запись</th>
              <th aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {items.map((call) => (
              <CallTableRow
                key={call.id}
                call={call}
                elevenLabsUsdPer1kCredits={elevenLabsUsdPer1kCredits}
                showCompany={showCompany}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.mobileCards} aria-label="Список звонков">
        {items.map((call) => (
          <CallMobileCard
            key={call.id}
            call={call}
            elevenLabsUsdPer1kCredits={elevenLabsUsdPer1kCredits}
            showCompany={showCompany}
          />
        ))}
      </div>
    </>
  )
}

function CallTableRow({
  call,
  elevenLabsUsdPer1kCredits,
  showCompany,
}: {
  call: AdminCallListItem
  elevenLabsUsdPer1kCredits: number
  showCompany: boolean
}) {
  const statusClass = callStatusClass(call.status)

  return (
    <tr>
      <td>
        <Link className={styles.link} to={`/admin/calls/${call.id}`}>
          № {call.id}
        </Link>
      </td>
      {showCompany && (
        <td>
          <Link className={styles.link} to={`/admin/companies/${call.companyId}`}>
            {call.companyName || `Компания № ${call.companyId}`}
          </Link>
        </td>
      )}
      <td>{callKindLabel(call.kind)}</td>
      <td>
        <span className={statusClass}>{callStatusLabel(call.status)}</span>
      </td>
      <td className={styles.mono}>{call.phone || '—'}</td>
      <td>{formatAdminDate(call.createdAt)}</td>
      <td>{formatDurationMs(call.durationMs)}</td>
      <td title={call.billingNote || undefined}>{billedLabel(call.billedMs)}</td>
      <td>
        <ElevenLabsCost
          credits={call.ttsCredits}
          usd={call.ttsUsd}
          rate={elevenLabsUsdPer1kCredits}
        />
      </td>
      <td>{formatApproxUsd(call.totalUsd)}</td>
      <td>{formatDurationMs(call.agentMs)}</td>
      <td>{formatDurationMs(call.humanMs)}</td>
      <td className={call.error ? styles.errorText : styles.muted}>{call.error || '—'}</td>
      <td>{call.hasRecording ? 'Есть' : 'Нет'}</td>
      <td>
        <DeleteCallButton call={call} />
      </td>
    </tr>
  )
}

function CallMobileCard({
  call,
  elevenLabsUsdPer1kCredits,
  showCompany,
}: {
  call: AdminCallListItem
  elevenLabsUsdPer1kCredits: number
  showCompany: boolean
}) {
  return (
    <article className={styles.mobileCard}>
      <MobileRow
        label="№"
        value={
          <Link className={styles.link} to={`/admin/calls/${call.id}`}>
            {call.id}
          </Link>
        }
      />
      {showCompany && (
        <MobileRow
          label="Компания"
          value={
            <Link className={styles.link} to={`/admin/companies/${call.companyId}`}>
              {call.companyName || `Компания № ${call.companyId}`}
            </Link>
          }
        />
      )}
      <MobileRow
        label="Статус"
        value={<span className={callStatusClass(call.status)}>{callStatusLabel(call.status)}</span>}
      />
      <MobileRow label="Дата" value={formatAdminDate(call.createdAt)} />
      <MobileRow label="Длительность" value={formatDurationMs(call.durationMs)} />
      <MobileRow label="Списано" value={billedLabel(call.billedMs)} />
      <MobileRow label="Стоимость" value={formatApproxUsd(call.totalUsd)} />
      <MobileRow
        label="Синтез"
        value={
          <ElevenLabsCost
            credits={call.ttsCredits}
            usd={call.ttsUsd}
            rate={elevenLabsUsdPer1kCredits}
          />
        }
      />
      <MobileRow label="Говорил агент" value={formatDurationMs(call.agentMs)} />
      <MobileRow label="Говорил клиент" value={formatDurationMs(call.humanMs)} />
      <MobileRow label="Тип" value={callKindLabel(call.kind)} />
      <MobileRow label="Телефон" value={call.phone || '—'} mono />
      {call.error && <MobileRow label="Ошибка" value={call.error} danger />}
      <MobileRow label="Запись" value={call.hasRecording ? 'Есть' : 'Нет'} />
    </article>
  )
}

function MobileRow({
  label,
  value,
  mono,
  danger,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
  danger?: boolean
}) {
  return (
    <div className={styles.mobileRow}>
      <span className={styles.mobileLabel}>{label}</span>
      <span
        className={`${styles.mobileValue} ${mono ? styles.mono : ''} ${danger ? styles.errorText : ''}`}
        data-admin-value
      >
        {value}
      </span>
    </div>
  )
}

function callStatusClass(status: string): string {
  const tone = callStatusTone(status)
  return [
    styles.status,
    tone === 'good' && styles.statusGood,
    tone === 'danger' && styles.statusDanger,
    tone === 'warn' && styles.statusWarn,
  ]
    .filter(Boolean)
    .join(' ')
}

/** Сколько списано с пакета: «—» — звонок в пакет не входит. */
function billedLabel(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—'
  if (ms === 0) return '0'
  return formatDurationMs(ms)
}

/** Кнопка удаления звонка: подтверждение, потом DELETE. */
export function DeleteCallButton({
  call,
  onDeleted,
}: {
  call: { id: number; status: string; phone?: string | null }
  onDeleted?: () => void
}) {
  const del = useAdminDeleteCall()
  const live = call.status === 'active' || call.status === 'pending'
  function submit() {
    const who = call.phone ? ` на ${call.phone}` : ''
    if (
      !window.confirm(
        `Удалить звонок № ${call.id}${who}?\n\nСтенограмма, разбор и запись удалятся насовсем, ` +
          'звонок пропадёт из статистики клиента, списанные минуты вернутся в пакет.',
      )
    )
      return
    del.mutate(call.id, { onSuccess: () => onDeleted?.() })
  }
  return (
    <button
      type="button"
      className={styles.dangerLink}
      onClick={submit}
      disabled={del.isPending}
      title={live ? 'Если звонок ещё идёт, удалить не получится' : 'Удалить звонок'}
    >
      {del.isPending ? 'Удаляю…' : 'Удалить'}
    </button>
  )
}
